/**
 * Module-scope coordination between the IDA entry point click, draft assistant
 * handlers, and queryResponseReceived.
 *
 * Tracks whether an IDA-initiated BizChat query is in flight, so that
 * queryResponseReceived can fire a 'BizChatResponseComplete' editor event
 * when the agent finishes streaming without writing content. Covers two cases:
 *   1. Agent calls get_draft but not rewrite_draft (asking for details)
 *   2. Agent never calls get_draft at all (e.g., no context prompt → chat-only response)
 *
 * Keyed by targetWindow (WeakMap) so concurrent compose sessions (e.g. multiple
 * popout windows) don't interfere. Each compose window has one BizChat session,
 * and all handler call sites (mark, clear, consume) receive targetWindow.
 * WeakMap also prevents memory leaks when popout windows close.
 *
 * A parallel Map<WindowKey, Window> allows the drawer-close orchestrator to
 * resolve Window from WindowKey for cancellation.
 *
 * Not in a Satchel store because this state does not drive UI rendering —
 * it only coordinates event flow between handler functions in this package.
 * (See wiki: "Do Not Use Satchel Store as Storage")
 */

import type { WindowKey } from 'owa-bizchat-types';
import { getBizChatWindowKey } from 'owa-copilot-chat-drawer-store';

interface PendingDraftRequest {
    editorId: string;
}

const pendingByWindow = new WeakMap<Window, PendingDraftRequest>();
const windowByKey = new Map<WindowKey, Window>();

/**
 * Called when the user clicks the IDA entry point and a BizChat query
 * will be auto-executed. Records the editor so queryResponseReceived
 * can fire the editor event if the agent responds without writing.
 */
export function markPendingDraftRequest(editorId: string, targetWindow: Window): void {
    pendingByWindow.set(targetWindow, { editorId });
    windowByKey.set(getBizChatWindowKey(targetWindow), targetWindow);
}

/**
 * Called when rewrite_draft succeeds. Clears the pending flag for the
 * given window because the agent wrote content — no "asking" transition needed.
 */
export function clearPendingDraftRequest(targetWindow: Window): void {
    pendingByWindow.delete(targetWindow);
    windowByKey.delete(getBizChatWindowKey(targetWindow));
}

/**
 * Called by queryResponseReceived. Returns the pending entry for the given
 * window if one exists, then removes it (one-shot). Returns undefined if
 * no pending request exists for that window.
 */
export function consumePendingDraftRequest(targetWindow: Window): PendingDraftRequest | undefined {
    const entry = pendingByWindow.get(targetWindow);
    if (entry) {
        pendingByWindow.delete(targetWindow);
        windowByKey.delete(getBizChatWindowKey(targetWindow));
    }
    return entry;
}

/**
 * Called by the drawer-close orchestrator. Resolves a WindowKey to the
 * pending entry's editorId, clears it, and returns the editorId.
 * Returns undefined if no pending request exists for that key.
 */
export function consumePendingDraftRequestByWindowKey(windowKey: WindowKey): string | undefined {
    const targetWindow = windowByKey.get(windowKey);
    if (!targetWindow) {
        return undefined;
    }
    const entry = pendingByWindow.get(targetWindow);
    if (entry) {
        pendingByWindow.delete(targetWindow);
        windowByKey.delete(windowKey);
        return entry.editorId;
    }
    return undefined;
}
